import React from "react";
import { Document, Page, View, Text, Image } from "@react-pdf/renderer";
import { styles, elab, statusColour } from "./pdfTheme";
import { thumbFor } from "./photos";
import { Cover, CoverDetails, Chrome, Heading, Narrative, ScoreAnalysis, OverallAssessment, Scope } from "./pdfSections";

/**
 * The Elaborate layout — each finding set out in full.
 *
 * The same report as Standard, at a different level of detail (design §7a).
 * Standard is a register: one row per finding, ~7 to a page, for long reports
 * and clients who want the list. Elaborate gives each finding its own block,
 * ~4 to a page, for short reports and findings that need explaining.
 *
 * Everything except section 7 is identical between the two layouts, so those
 * sections live in pdfSections.js and are imported by both. Only the findings
 * differ, which is the point: a client comparing a Standard and an Elaborate
 * report of the same audit must not find different numbers in them.
 *
 * The entry format is the one in the design:
 *
 *     7.4.2   Non-conformant
 *             Dangerous parts of machinery are not securely guarded,
 *             exposing operators to entanglement and crush injury.
 *             Observed: interlock bypassed on press line 3.
 *             Action:   restore fixed and interlocked guarding.
 *             OSH Code 2020 / State Factory Rules        [2 photos]
 */



/* ------------------------------------------------------------------ */
/* one finding                                                         */
/* ------------------------------------------------------------------ */

/**
 * One finding, as a ruled table.
 *
 * Every row is label-then-value in the same two columns, so an eye running
 * down forty findings looking for "Action" finds it in the same place each
 * time. The previous layout set the labels inline, which reads well for a
 * single entry and turns into a wall at length.
 *
 * Rows appear only when they carry something. A conformant finding has no
 * Action, and printing an empty one would suggest something was missed rather
 * than that nothing needed fixing.
 *
 * wrap={false} keeps a finding whole: half a table at the foot of a page, with
 * its Action overleaf, is how a reader ends up attributing one finding's
 * remedy to another.
 */
function Finding({ f }) {
  const rows = [];
  if (f.statement) rows.push(["Finding", f.statement]);
  if (f.note) rows.push(["Observed", f.note]);
  if (f.recommendation) rows.push(["Action", f.recommendation]);
  if (f.legal_reference) rows.push(["Legal reference", f.legal_reference]);
  if (f.verification_method) rows.push(["Verification", f.verification_method]);

  const photos = (f.photos || []).slice(0, 6);
  const lastTextRow = photos.length ? -1 : rows.length - 1;

  return (
    <View style={elab.tbl} wrap={false}>
      <View style={elab.tHead}>
        <Text style={elab.tNumber}>{f.number}</Text>
        <Text style={[elab.tStatus, { color: statusColour(f.status) }]}>
          {f.status_label}
        </Text>
      </View>

      {rows.map(([label, value], i) => (
        <View key={label} style={i === lastTextRow ? elab.tRowLast : elab.tRow}>
          <Text style={elab.tLabel}>{label}</Text>
          <Text style={elab.tValue}>{value}</Text>
        </View>
      ))}

      {photos.length ? (
        <View style={elab.tRowLast}>
          <Text style={elab.tLabel}>Evidence</Text>
          <View style={elab.tPhotos}>
            {photos.map((u, k) => (
              <Image key={k} src={thumbFor(u)} style={elab.photo} />
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* section 7                                                           */
/* ------------------------------------------------------------------ */

function Findings({ section, header }) {
  const d = section.data;

  return (
    <Page size="A4" style={styles.page}>
      <Chrome header={header} />
      <Heading no={section.no} title={section.title} />
      <Text style={[styles.muted, { marginBottom: 10 }]}>
        {d.selected} finding{d.selected === 1 ? "" : "s"} shown, from {d.assessed} questions
        assessed.
      </Text>

      {!d.pillars.length ? (
        <Text style={styles.empty}>No findings were selected for this report.</Text>
      ) : null}

      {d.pillars.map((p) => (
        <View key={p.name}>
          <Text style={elab.pillar}>{p.name}</Text>
          {p.sub_pillars.map((s) => (
            <View key={s.name}>
              <Text style={elab.subPillar}>{s.name}</Text>
              {s.findings.map((f) => (
                <Finding key={f.number} f={f} />
              ))}
            </View>
          ))}
        </View>
      ))}
    </Page>
  );
}

/* ------------------------------------------------------------------ */

export default function CsaElaboratePdf({ model }) {
  const { header, summary, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const renderSection = (s) => {
    if (s.key === "score_analysis") return <ScoreAnalysis key={s.key} section={s} />;
    if (s.key === "overall_assessment") return <OverallAssessment key={s.key} section={s} />;
    if (s.key === "scope") return <Scope key={s.key} section={s} />;
    return <Narrative key={s.key} section={s} />;
  };

  const before = sections.filter((s) => s.no < 7);
  const after = sections.filter((s) => s.no > 7);

  return (
    <Document
      title={`CSA — ${header.organization} — ${header.site}`}
      author={header.prepared_by || "Momentum"}
      subject="Critical Safety Assessment"
    >
      <Cover header={header} summary={summary} />

      {/* What the cover used to carry. The branded frame has room for four
          lines and a title, so these moved rather than went. */}
      <Page size="A4" style={styles.page}>
        <Chrome header={header} />
        <CoverDetails header={header} summary={summary} />
      </Page>

      {before.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}

      {/*
        Portrait, unlike Standard. Elaborate has no ten-column table to fit, so
        there is nothing to turn the page for — and prose reads better in
        portrait, which is the whole reason to pick this layout.
      */}
      <Findings section={byKey("findings")} header={header} />

      {after.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}
    </Document>
  );
}
